import { useState } from 'react';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import { formatMillimes } from '../../lib/money';
import type { InvokeResponseBase } from '../../lib/types';

const SAMPLES = [
  { label: 'SARL textile (Sahel)', mf: '1234567A/M/A/000' },
  { label: 'SA banque', mf: '0987654B/N/A/001' },
  { label: 'EI auto-entrepreneur', mf: '7777777D/N/A/000' },
  { label: 'Exploitation agricole', mf: '3333333F/N/A/000' },
  { label: 'Société radiée', mf: '9999999E/N/A/000' },
  { label: 'Matricule introuvable', mf: '0000001Z/N/A/999' },
];

interface OnboardeurResult {
  company: {
    name: string;
    legal_name: string;
    matricule_fiscal: string;
    rne_number: string;
    legal_form: string;
    activity_sector: string;
    activity_label: string;
    capital_millimes: number;
    date_creation: string;
    proposed_tax_regime: string;
    regime_confidence: number;
    regime_reasoning: string;
  };
  contacts: Array<{ contact_type: string; full_name: string }>;
  proposed_revenue_account: { sce_code: string; confidence: number; reasoning: string };
  source_rne_cached_at: string;
}

export function OnboardeurPage() {
  const [matricule, setMatricule] = useState('1234567A/M/A/000');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const res = await api.invokeAgent('onboardeur', {
        payload: { matricule_fiscal: matricule },
        fiscal_reference_date: new Date().toISOString().slice(0, 10),
      });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AgentPageShell
      title="Onboardeur"
      module="Extension · Phase 1"
      model="Haiku 4.5"
      description={
        <>
          Crée un dossier client à partir d'un matricule fiscal. Lookup RNE (mode fixture en bootstrap),
          classification du compte de produit principal via SCE NC 01, suggestion de régime fiscal via
          Haiku. Escalade automatique sur homonymie / radiation.
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard title="Entrée">
          <Field label="Matricule fiscal" hint="Format : NNNNNNNL/L/L/NNN (7 chiffres + 3 lettres + 3 chiffres)">
            <input
              className={inputCls + ' font-mono'}
              value={matricule}
              onChange={(e) => setMatricule(e.target.value.toUpperCase())}
              placeholder="1234567A/M/A/000"
              required
            />
          </Field>
          <div className="text-xs text-taupe">
            Échantillons rapides :
            <div className="flex flex-wrap gap-2 mt-2">
              {SAMPLES.map((s) => (
                <button
                  type="button"
                  key={s.mf}
                  onClick={() => setMatricule(s.mf)}
                  className="px-2 py-1 text-xs rounded border border-border bg-white hover:border-gold transition"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </FormCard>

        <div className="flex gap-3">
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Invocation…' : 'Invoquer Onboardeur'}
          </button>
        </div>
      </form>

      {error && (
        <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>
      )}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <OnboardeurResultView result={result as OnboardeurResult} />}
        />
      )}
    </AgentPageShell>
  );
}

function OnboardeurResultView({ result }: { result: OnboardeurResult }) {
  return (
    <div className="space-y-4">
      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Entreprise</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <Row label="Raison sociale" value={result.company.legal_name} />
          <Row label="Forme juridique" value={result.company.legal_form} />
          <Row label="Matricule fiscal" value={result.company.matricule_fiscal} mono />
          <Row label="N° RNE" value={result.company.rne_number} mono />
          <Row label="Secteur d'activité" value={result.company.activity_sector} />
          <Row label="Activité" value={result.company.activity_label} />
          <Row label="Capital social" value={formatMillimes(result.company.capital_millimes)} />
          <Row label="Date de création" value={result.company.date_creation} />
        </div>
      </section>
      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Régime fiscal proposé</div>
        <div className="p-3 rounded bg-cream/50 border border-border">
          <div className="font-serif text-lg font-bold capitalize">{result.company.proposed_tax_regime}</div>
          <div className="text-xs text-taupe mt-0.5">
            confiance {(result.company.regime_confidence * 100).toFixed(0)}% — {result.company.regime_reasoning}
          </div>
        </div>
      </section>
      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Compte de ventes proposé (SCE)</div>
        <div className="p-3 rounded bg-cream/50 border border-border flex items-baseline gap-3">
          <span className="font-mono text-xl font-bold">{result.proposed_revenue_account.sce_code}</span>
          <span className="text-xs text-taupe">
            confiance {(result.proposed_revenue_account.confidence * 100).toFixed(0)}% — {result.proposed_revenue_account.reasoning}
          </span>
        </div>
      </section>
      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Dirigeants & associés</div>
        <ul className="text-sm space-y-1">
          {result.contacts.map((c, i) => (
            <li key={i}>
              <span className="text-xs uppercase tracking-wider text-taupe mr-2">{c.contact_type}</span>
              {c.full_name}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs text-taupe">{label}</div>
      <div className={mono ? 'font-mono' : ''}>{value}</div>
    </div>
  );
}
